/**
 * The dashboard's write-affordance gates (Feature: granular-crud-api, task 14.2; Requirement 11.7;
 * Design: §11. Extended for user-and-data-management Req 5.8).
 *
 * These are **display only**: they decide whether to render a control, never whether a write is
 * allowed. The Soroush_API authorizes every write through its own fail-closed role matrix, which is why
 * a viewer who reached a write route anyway would still be refused a `403`.
 *
 * ## The role comes from the SELECTED Account, not from the token
 *
 * It used to come from the ID token's `role` claim, and that was correct while the only Account a
 * request could name was the token's own. The Account switcher (Req 5.8) ended that: the token's `role`
 * is the **home** Account's, and for a shared Account the server takes the role from the membership row
 * instead. Reading the claim would now be wrong in both directions —
 *
 *  - `admin` at home, `viewer` in the selected Account: every write control rendered, every write
 *    refused. An affordance that looks available and is not, which the note on
 *    {@link useCanAdministerMembers} already argues is worse than one that is absent.
 *  - `viewer` at home, `editor` in the selected Account: controls withheld that the server would allow.
 *
 * — so both gates read {@link useSelectedAccount}, whose `role` is whatever the API said for that
 * Account. The eleven call sites did not change: the indirection was already here.
 *
 * Fail-closed to match the server: anything that is not `editor` or `admin` — an absent role, an
 * unrecognized one, or the window before the Account list has loaded — reads as no write access.
 */

import { useSelectedAccount } from '../account-context';

/** The roles the API's authorization matrix grants the Check / Alert_Rule write actions (Req 9.3). */
const WRITE_ROLES: readonly string[] = ['editor', 'admin'];

/** Whether the User's role **in the selected Account** may perform Granular_CRUD_API writes (Req 11.7). */
export function useCanWrite(): boolean {
  const { role } = useSelectedAccount();
  return role !== undefined && WRITE_ROLES.includes(role);
}

/**
 * The roles the API grants `member:administer` — inviting, changing a role, removing a member
 * (user-and-data-management Req 5.6).
 *
 * Deliberately **narrower** than {@link WRITE_ROLES}: managing who can see an Account's data is not the
 * same permission as editing what it monitors, and Requirement 5.6 restricts it to an administrator.
 * `member:read` is not gated at all, because Requirement 6 is premised on knowing who can see your data
 * and that is not a privileged fact.
 */
const ADMINISTER_ROLES: readonly string[] = ['admin'];

/**
 * Whether the User may manage membership **of the selected Account** (Req 5.6).
 *
 * Display only, exactly as {@link useCanWrite} is — the server is the enforcement point. Kept separate
 * rather than folded into `useCanWrite` because using the write gate here would render Invite, the role
 * select, and Remove for an **editor**, who would then get a `403` from all three: an affordance that
 * looks available and is not, which is worse than one that is absent.
 *
 * This is the gate the switcher makes most visible. An admin of their own Account who switches to one
 * where they are a viewer must lose the Invite button, not merely be refused when they press it.
 */
export function useCanAdministerMembers(): boolean {
  const { role } = useSelectedAccount();
  return role !== undefined && ADMINISTER_ROLES.includes(role);
}
